import { Delete, Add } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Stack,
} from "@mui/material";
import useStyles from "./styles";
import { canUseFeatures } from "../../utils/utils";
import { useState, useEffect } from "react";
import { THEME_COLORS } from "../../context/constants";
import { Script, Workspace } from "../../context/types";
import Papa from "papaparse";
import Message from "../Message";
import VariableInfo from "./components/VariableInfo";
import VariablesFileUpload from "./components/VariablesFileUpload";

const VariableModal = ({
  isOpen,
  onClose,
  currentScript,
  setCurrentScript,
  onSave,
  currentWorkspace,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentScript: Script | null;
  setCurrentScript: any;
  onSave: any;
  currentWorkspace: Workspace | null;
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [newVariableName, setNewVariableName] = useState("");
  const [newVariableValue, setNewVariableValue] = useState("");
  const [correlationMode, setCorrelationMode] = useState(false);
  const [selectedForCorrelation, setSelectedForCorrelation] = useState<{
    variableName: string;
    value: string;
  } | null>(null);
  const [showAddVariableTab, setShowAddVariableTab] = useState(
    !currentScript?.variableGroups?.[0]?.variables?.length
  );
  const [autoCorrelateByIndex, setAutoCorrelateByIndex] = useState(true);

  // Migrate to variableGroups if using legacy variables
  const [workingScript, setWorkingScript] = useState(() => {
    if (currentScript?.variableGroups) return currentScript;
    return {
      ...currentScript,
      variableGroups: [
        {
          name: "Default Group",
          variables: (currentScript?.variables || []).map((v: any) => ({
            name: v?.name,
            values: v.values.map((val: string) => ({
              value: val,
              correlations: {},
            })),
          })),
        },
      ],
    };
  });

  const [primaryGroup, setPrimaryGroup] = useState(
    workingScript.variableGroups?.[0] || {
      name: "Default Group",
      variables: [],
    }
  );
  const [variables, setVariables] = useState(primaryGroup.variables || []);
  const [maxVariablesReached, setMaxVariablesReached] = useState(false);

  const handleVariablesFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsedData = await new Promise<string[][]>((resolve, reject) => {
        Papa.parse(file, {
          complete: (result: any) => resolve(result.data),
          error: (error) => reject(error),
          header: false,
        });
      });

      if (parsedData.length === 0) return;

      setWorkingScript((prev: any) => {
        if (!prev.variableGroups || prev.variableGroups.length === 0) {
          return prev;
        }

        // Create a deep copy of the variable groups
        const newGroups = JSON.parse(JSON.stringify(prev.variableGroups));

        // Ensure we have a variable at the activeTab index
        if (!newGroups[0].variables[activeTab]) {
          return prev;
        }

        const currentVariable = newGroups[0].variables[activeTab];

        // Get existing values to avoid duplicates
        const existingValues = currentVariable.values.map((v: any) => v.value);

        // Add new values from CSV
        const newValues = parsedData
          .flat()
          .filter(
            (value) =>
              value &&
              value.trim() !== "" &&
              !existingValues.includes(value.trim())
          )
          .map((value) => ({
            value: value.trim(),
            correlations: {},
          }));

        if (newValues.length === 0) return prev;

        // Add the new values to the current variable
        currentVariable.values = [...currentVariable.values, ...newValues];

        // If auto-correlation is enabled and we're not on the first variable
        if (autoCorrelateByIndex && activeTab !== 0) {
          const primaryVariable = newGroups[0].variables[0];
          newValues.forEach((newVal, index) => {
            // Find the corresponding primary value to correlate with
            const primaryValueIndex = Math.min(
              index,
              primaryVariable.values.length - 1
            );
            const primaryValue =
              primaryVariable.values[primaryValueIndex]?.value;

            if (primaryValue) {
              // Update the correlation in the primary variable
              primaryVariable.values[primaryValueIndex].correlations = {
                ...primaryVariable.values[primaryValueIndex].correlations,
                [currentVariable?.name]: newVal.value,
              };
            }
          });
        }

        return {
          ...prev,
          variableGroups: newGroups,
        };
      });

      // Reset the file input
      if (event.target) event.target.value = "";
    } catch (error) {
      console.error("Error processing CSV:", error);
    }
  };
  // Update derived state when workingScript changes
  useEffect(() => {
    const newPrimaryGroup = workingScript.variableGroups?.[0] || {
      name: "Default Group",
      variables: [],
    };
    setPrimaryGroup(newPrimaryGroup);
    setVariables(newPrimaryGroup.variables || []);
    setMaxVariablesReached(newPrimaryGroup.variables?.length >= 2);
    // Show add tab if no variables exist
    setShowAddVariableTab(newPrimaryGroup.variables?.length === 0);
    setCurrentScript(workingScript);
  }, [workingScript]);

  // Enable correlation mode if we have multiple variables
  useEffect(() => {
    if (!workingScript.variableGroups) return;
    if (workingScript.variableGroups?.[0]?.variables?.length > 1) {
      setCorrelationMode(true);
    } else {
      setCorrelationMode(false);
    }
  }, [workingScript]);

  // Automatically correlate values by index when variables change
  const updateCorrelationsByIndex = (script: any) => {
    if (
      !autoCorrelateByIndex ||
      !script.variableGroups?.[0]?.variables ||
      script.variableGroups[0].variables.length < 2
    ) {
      return script;
    }

    const newGroups = [...script.variableGroups];
    const primaryVariable = newGroups[0].variables[0];

    primaryVariable.values.forEach((val: any, index: number) => {
      script.variableGroups[0].variables.slice(1).forEach((targetVar: any) => {
        const targetValue =
          targetVar.values[Math.min(index, targetVar.values.length - 1)]?.value;
        if (targetValue) {
          val.correlations = {
            ...val.correlations,
            [targetVar?.name]: targetValue,
          };
        }
      });
    });

    return {
      ...script,
      variableGroups: newGroups,
    };
  };

  // Apply correlation updates when needed
  useEffect(() => {
    if (autoCorrelateByIndex) {
      setWorkingScript((prev) => updateCorrelationsByIndex(prev));
    }
  }, [variables, autoCorrelateByIndex]);

  const handleAddVariable = () => {
    if (!newVariableName.trim() || maxVariablesReached) return;

    setWorkingScript((prev: any) => {
      // Ensure variableGroups exists
      const newGroups = prev.variableGroups
        ? [...prev.variableGroups]
        : [{ name: "Default Group", variables: [] }];

      // Ensure first group exists
      if (newGroups.length === 0) {
        newGroups.push({ name: "Default Group", variables: [] });
      }

      // Ensure variables array exists
      if (!newGroups[0].variables) {
        newGroups[0].variables = [];
      }

      // Check for existing variable
      const existingVarIndex = newGroups[0].variables.findIndex(
        (v: any) =>
          v?.name?.toLowerCase() === newVariableName.trim().toLowerCase()
      );

      if (existingVarIndex === -1) {
        newGroups[0].variables.push({
          name: newVariableName.trim(),
          values: [],
        });
      }

      const updatedScript = {
        ...prev,
        variableGroups: newGroups,
        variables: undefined, // Clear old format
      };

      return autoCorrelateByIndex
        ? updateCorrelationsByIndex(updatedScript)
        : updatedScript;
    });

    setNewVariableName("");
    // Set active tab to the new variable
    setActiveTab(variables?.length ? variables?.length - 1 : 0);
    setShowAddVariableTab(false);
  };

  const handleRemoveVariable = (variableIndex: number) => {
    setWorkingScript((prev: any) => {
      const newGroups = [...prev.variableGroups];
      const removedVariable = newGroups[0].variables[variableIndex];

      newGroups[0].variables.splice(variableIndex, 1);

      if (variableIndex === 0) {
        newGroups[0].variables.forEach((v: any) => {
          v.values.forEach((val: any) => {
            val.correlations = {};
          });
        });
      } else {
        const primaryVariable = newGroups[0].variables[0];
        if (primaryVariable) {
          primaryVariable.values.forEach((val: any) => {
            const { [removedVariable.name]: _, ...rest } = val.correlations;
            val.correlations = rest;
          });
        }
      }

      const updatedScript = {
        ...prev,
        variableGroups: newGroups,
        variables: undefined,
      };

      return autoCorrelateByIndex
        ? updateCorrelationsByIndex(updatedScript)
        : updatedScript;
    });

    if (activeTab >= variableIndex) {
      setActiveTab(Math.max(0, activeTab - 1));
    }
  };

  const handleAddValue = () => {
    if (!newVariableValue.trim()) return;

    setWorkingScript((prev: any) => {
      const newGroups = [...prev.variableGroups];
      const variable = newGroups[0].variables[activeTab];
      variable.values.push({
        value: newVariableValue.trim(),
        correlations: {},
      });

      const updatedScript = { ...prev, variableGroups: newGroups };
      return autoCorrelateByIndex
        ? updateCorrelationsByIndex(updatedScript)
        : updatedScript;
    });

    setNewVariableValue("");
  };

  const handleRemoveValue = (variableIndex: number, valueIndex: number) => {
    setWorkingScript((prev: any) => {
      const newGroups = [...prev.variableGroups];
      const variable = newGroups[0].variables[variableIndex];

      variable.values.splice(valueIndex, 1);

      if (variableIndex === 0) {
        newGroups[0].variables.slice(1).forEach((targetVar: any) => {
          if (targetVar.values[valueIndex]) {
            targetVar.values.splice(valueIndex, 1);
          }
        });

        variable.values.forEach((val: any) => {
          Object.keys(val.correlations).forEach((targetVarName) => {
            const targetVar = newGroups[0].variables.find(
              (v: any) => v.name === targetVarName
            );
            if (
              !targetVar ||
              !targetVar.values.some(
                (v: any) => v.value === val.correlations[targetVarName]
              )
            ) {
              const { [targetVarName]: _, ...rest } = val.correlations;
              val.correlations = rest;
            }
          });
        });
      }

      const updatedScript = { ...prev, variableGroups: newGroups };
      return autoCorrelateByIndex
        ? updateCorrelationsByIndex(updatedScript)
        : updatedScript;
    });
  };

  const handleSetCorrelation = (
    sourceVarIndex: number,
    sourceValue: string,
    targetVarIndex: number,
    targetValue: string
  ) => {
    setWorkingScript((prev: any) => {
      const newGroups = [...prev.variableGroups];
      const sourceValueObj = newGroups[0].variables[sourceVarIndex].values.find(
        (v: any) => v.value === sourceValue
      );

      if (sourceValueObj) {
        const targetVarName = newGroups[0].variables[targetVarIndex]?.name;
        sourceValueObj.correlations = {
          ...sourceValueObj.correlations,
          [targetVarName]: targetValue,
        };
      }

      return { ...prev, variableGroups: newGroups };
    });
  };

  const handleSave = () => {
    onSave(workingScript);
    onClose();
  };

  const handleAddVariableTabClick = () => {
    setShowAddVariableTab(true);
    setActiveTab(variables.length);
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="add-variable-modal"
      aria-describedby="add-values-to-variable">
      <Box className={classes.modalContent}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex" }}>
          <VariableInfo />
          {variables?.length > 1
            ? "Manage Variables"
            : variables.length === 1
            ? `Manage ${variables?.[0]?.name}`
            : "Add Variables"}
        </Typography>
        {!maxVariablesReached && showAddVariableTab && (
          <VariablesFileUpload
            setWorkingScript={setWorkingScript}
            workingScript={workingScript}
            activeTab={activeTab}
            autoCorrelateByIndex={autoCorrelateByIndex}
            maxVariablesReached={maxVariablesReached}
            showAddVariableTab={showAddVariableTab}
          />
        )}

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              color: THEME_COLORS.yellow,
              "& .MuiTab-root": { color: "black" },
              "& .Mui-selected": { color: THEME_COLORS.yellow },
              flexGrow: 1,
            }}>
            {variables.map((variable: any, index: number) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {variable?.name}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveVariable(index);
                      }}
                      sx={{ ml: 1, color: "error.main" }}>
                      <Delete
                        fontSize="small"
                        sx={{ color: THEME_COLORS.yellow }}
                      />
                    </IconButton>
                  </Box>
                }
                sx={{ textTransform: "none" }}
              />
            ))}
            {!maxVariablesReached && showAddVariableTab && (
              <Tab label="Add Variable" sx={{ textTransform: "none" }} />
            )}
          </Tabs>
          {!maxVariablesReached && !showAddVariableTab && (
            <IconButton
              onClick={handleAddVariableTabClick}
              color="primary"
              sx={{ ml: 1 }}>
              <Add />
            </IconButton>
          )}
        </Box>
        {!!variables?.[activeTab]?.name && (
          <Box mt={2}>
            <Message
              text={`Place variables in your script using double curly braces, like {{${variables?.[activeTab].name}}}. Each value you add here will create a different video version.`}
              type="hint"
            />
          </Box>
        )}
        {variables.length > 0 && activeTab < variables.length && (
          <>
            <Box marginTop={2}>
              <Typography variant="subtitle1" gutterBottom>
                Values for {variables[activeTab].name}
              </Typography>

              <Box display="flex" gap={1} marginY={2}>
                <TextField
                  fullWidth
                  label="Add new value"
                  value={newVariableValue}
                  onChange={(e) => setNewVariableValue(e.target.value)}
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleAddValue}
                  disabled={
                    !newVariableValue.trim() ||
                    !canUseFeatures(currentWorkspace)
                  }>
                  Add
                </Button>
              </Box>

              <Box marginBottom="20px">
                <Typography variant="subtitle2" gutterBottom>
                  Or upload from CSV:
                </Typography>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleVariablesFileUpload}
                  style={{ display: "none" }}
                  id={`csv-upload-${activeTab}`} // Unique key per tab
                  key={`csv-upload-${activeTab}`} // Force re-render when tab changes
                />
                <label htmlFor={`csv-upload-${activeTab}`}>
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={!canUseFeatures(currentWorkspace)}>
                    Upload CSV
                  </Button>
                </label>
              </Box>

              {variables[activeTab].values.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                  {variables[activeTab].values.map(
                    (val: any, valIndex: number) => (
                      <Box
                        key={valIndex}
                        sx={{
                          p: 1,
                          mb: 1,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                        <Box>
                          <Typography>{val.value}</Typography>
                          {correlationMode && activeTab === 0 && (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <Typography variant="caption" mr={1}>
                                Correlates with:
                              </Typography>
                              {variables
                                .slice(1)
                                .map((targetVar: any, targetIndex: number) => (
                                  <Chip
                                    key={targetIndex}
                                    label={`${targetVar.name}: ${
                                      val.correlations?.[targetVar?.name] ||
                                      "none"
                                    }`}
                                    size="small"
                                    sx={{ mr: 1 }}
                                    onClick={() => {
                                      setSelectedForCorrelation({
                                        variableName:
                                          variables[activeTab]?.name,
                                        value: val.value,
                                      });
                                    }}
                                  />
                                ))}
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveValue(activeTab, valIndex)
                          }>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No values added yet.
                </Typography>
              )}
            </Box>

            {activeTab === 0 && variables.length > 1 && (
              <Box mt={2}>
                <Tooltip title="When checked, each value will automatically pair with values at matching positions in other variables. Extra values will link to the last available option.">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoCorrelateByIndex}
                        onChange={(e) =>
                          setAutoCorrelateByIndex(e.target.checked)
                        }
                        color="primary"
                        size="small"
                      />
                    }
                    label="Correlate with same index"
                    sx={{ fontSize: "16px" }}
                  />
                </Tooltip>
              </Box>
            )}
          </>
        )}

        {!maxVariablesReached &&
          showAddVariableTab &&
          activeTab === variables.length && (
            <Box marginTop={2}>
              <Typography variant="subtitle1" gutterBottom>
                Add New Variable
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  label="New Variable Name"
                  value={newVariableName}
                  onChange={(e) => setNewVariableName(e.target.value)}
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleAddVariable}
                  disabled={
                    !newVariableName.trim() || !canUseFeatures(currentWorkspace)
                  }>
                  Add
                </Button>
              </Box>
            </Box>
          )}

        <Modal
          open={!!selectedForCorrelation}
          onClose={() => setSelectedForCorrelation(null)}>
          <Box className={classes.modalContent} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Set Correlation for "{selectedForCorrelation?.value}"
            </Typography>
            {variables.slice(1).map((targetVar: any) => (
              <Box key={targetVar?.name} mb={2}>
                <Typography variant="subtitle1">{targetVar?.name}</Typography>
                <Select
                  fullWidth
                  value={
                    selectedForCorrelation &&
                    !!workingScript?.variableGroups?.[0]?.variables?.[0]
                      ? workingScript.variableGroups[0].variables[0].values.find(
                          (v: any) => v.value === selectedForCorrelation.value
                        )?.correlations?.[targetVar?.name] || ""
                      : ""
                  }
                  onChange={(e) => {
                    if (selectedForCorrelation) {
                      handleSetCorrelation(
                        0,
                        selectedForCorrelation.value,
                        variables.findIndex(
                          (v: any) => v?.name === targetVar?.name
                        ),
                        e.target.value
                      );
                    }
                  }}>
                  <MenuItem value="">None</MenuItem>
                  {targetVar.values.map((val: any) => (
                    <MenuItem key={val.value} value={val.value}>
                      {val.value}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={() => setSelectedForCorrelation(null)}>
              Done
            </Button>
          </Box>
        </Modal>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button onClick={onClose} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canUseFeatures(currentWorkspace)}>
            Save Changes
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default VariableModal;
